import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Table, Badge } from 'react-bootstrap';
import { getTeamStandings, GroupStanding, TeamStanding, addTeamsToStandings, AddTeamsToStandingsRequest } from '../../api/tournament/standingsService';
import { listTeams } from '../../api/tournament/teamService';
import { getAllAgeCategoriesPaginated } from '../../api/tournament/ageCategoryService';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TeamManagementCard from '../../Common/TeamManagementCard';
import '../../Common/TournamentHeader.css';
import '../../Common/ManageTournamentsFixturesTable.css';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';

const GroupStandingsDetailPage: React.FC = () => {
  const { tournamentId, ageCategoryId } = useParams<{ tournamentId: string; ageCategoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [groupStandings, setGroupStandings] = useState<GroupStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add Group State
  const [showAddGroupCard, setShowAddGroupCard] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState(false);
  
  // Get age category info, view mode, tournamentSeasonId, and fromTournamentDetailPage flag from location state
  const [ageCategoryInfo, setAgeCategoryInfo] = useState(location.state?.ageCategoryInfo);
  const isViewMode = location.state?.viewMode || false;
  const tournamentSeasonId = location.state?.tournamentSeasonId; // Get tournamentSeasonId for back navigation
  const fromTournamentDetailPage = location.state?.fromTournamentDetailPage || false; // Check if we came from tournament detail page (default season)
  
  // Get sport from location state/URL to preserve it during navigation
  let currentSport: string | undefined;
  try {
    currentSport = getSportValueUtil(null, location);
  } catch {
    // If sport cannot be determined, try to get from URL params
    const urlParams = new URLSearchParams(location.search);
    currentSport = urlParams.get('sport') || undefined;
  }
  
  // Preserve selectedTournament from location state for navigation
  const selectedTournament = (location.state as any)?.selectedTournament || (currentSport ? { sports: currentSport } : undefined);
  const groupDetailSearchParams = new URLSearchParams(location.search);
  if (currentSport) {
    groupDetailSearchParams.set('sport', currentSport);
  }
  if (tournamentSeasonId) {
    groupDetailSearchParams.set('seasonId', tournamentSeasonId);
  }
  const groupDetailSearch = groupDetailSearchParams.toString();

  const fetchAgeCategoryInfo = useCallback(async () => {
    try {
      // Get sport from location state or URL params
      const selectedTournamentFromState = (location.state as any)?.selectedTournament;
      const sportFromState = selectedTournamentFromState?.sports;
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      // Use shared utility - throws error if sport cannot be determined
      const sport = getSportValueUtil(null, location);
      
      // Pass tournamentSeasonId when available (from location.state)
      const response = await getAllAgeCategoriesPaginated(tournamentId!, 1, 1000, '', '', '', tournamentSeasonId || undefined, sport);
      
      let ageCategoriesData = null;
      if (response && response.paginatedResults) {
        ageCategoriesData = response.paginatedResults;
      } else if (response && response.data && response.data.paginatedResults) {
        ageCategoriesData = response.data.paginatedResults;
      } else if (Array.isArray(response)) {
        ageCategoriesData = response;
      }
      
      if (ageCategoriesData && ageCategoriesData.length > 0) {
        const foundAgeCategory = ageCategoriesData.find((ac: any) => ac._id === ageCategoryId);
        
        if (foundAgeCategory) {
          const newAgeCategoryInfo = {
            _id: foundAgeCategory._id,
            ageCategory: foundAgeCategory.ageCategory,
            standingsType: foundAgeCategory.standingsType
          };
          setAgeCategoryInfo(newAgeCategoryInfo);
        }
      }
    } catch (error) {
      console.error('Error fetching age category info:', error);
    }
  }, [tournamentId, ageCategoryId]);

  useEffect(() => {
    if (tournamentId && ageCategoryId) {
      loadStandings();
      
      if (!ageCategoryInfo) {
        fetchAgeCategoryInfo();
      }
    }
  }, [tournamentId, ageCategoryId, ageCategoryInfo]);

  // Set view mode from location state
  useEffect(() => {
    setViewMode(isViewMode);
  }, [isViewMode]);

  const loadStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get sport for API call
      const sport = getSportValueUtil(null, location);
      const seasonId = tournamentSeasonId || new URLSearchParams(location.search).get('seasonId') || undefined;
      const response = await getTeamStandings(ageCategoryId!, sport, seasonId);
      
      if (!response) {
        setError('No response received from server');
        return;
      }
      
      if (Array.isArray(response)) {
        if (response.length === 0) {
          setGroupStandings([]);
          return;
        }
        
        const isGroupStandings = response[0].hasOwnProperty('groupName');
        
        if (isGroupStandings) {
          setGroupStandings(response as GroupStanding[]);
        } else {
          setError('Expected group standings but received single standings');
        }
      } else {
        setError(`Invalid response format. Expected array but received: ${typeof response}`);
      }
    } catch (err: any) {
      console.error('Error loading standings:', err);
      if (err.response) {
        setError(`API Error: ${err.response.status} - ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('Network Error: Unable to reach server');
      } else {
        setError(`Error: ${err.message || 'Failed to load standings'}`);
      }
    } finally {
      setLoading(false);
    }
  };


  // Handle opening the add group card
  const handleOpenAddGroupCard = () => {
    setShowAddGroupCard(true);
    setNewGroupName('');
  };

  // Handle adding group with teams (called by TeamManagementCard)
  const handleAddGroup = async () => {
    // This will be called by TeamManagementCard after teams are added
    // The actual team addition is handled by TeamManagementCard itself
      await loadStandings();
      setShowAddGroupCard(false);
      setNewGroupName('');
  };

  const handleViewGroup = (group: GroupStanding) => {
    // Navigate to group detail page in view mode
    navigate(`/manage-tournaments-fixtures/standings/group-detail/${tournamentId}/${ageCategoryId}/${group.groupName}${groupDetailSearch ? `?${groupDetailSearch}` : ''}`, {
      state: { 
        ageCategoryInfo,
        viewMode: true,
        tournamentSeasonId, // Pass tournamentSeasonId to filter teams by season
        fromTournamentDetailPage, // Pass fromTournamentDetailPage flag
        selectedTournament
      }
    });
  };

  const handleEditGroup = (group: GroupStanding) => {
    // Navigate to group detail page in edit mode
    navigate(`/manage-tournaments-fixtures/standings/group-detail/${tournamentId}/${ageCategoryId}/${group.groupName}${groupDetailSearch ? `?${groupDetailSearch}` : ''}`, {
      state: { 
        ageCategoryInfo,
        viewMode: false,
        tournamentSeasonId, // Pass tournamentSeasonId to filter teams by season
        fromTournamentDetailPage, // Pass fromTournamentDetailPage flag
        selectedTournament
      }
    });
  };

  const filteredGroups = groupStandings;

  const getPositionBadge = (points: number, standings: TeamStanding[], currentIndex: number) => {
    const sortedStandings = [...standings].sort((a, b) => b.points - a.points);
    const position = sortedStandings.findIndex(s => s.team._id === standings[currentIndex].team._id) + 1;
    
    if (position === 1) return <Badge bg="warning">1st</Badge>;
    if (position === 2) return <Badge bg="secondary">2nd</Badge>;
    if (position === 3) return <Badge bg="danger">3rd</Badge>;
    return <Badge bg="light" text="dark">{position}th</Badge>;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" rows={10} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments-fixtures') },
              { 
                label: 'Tournament Details', 
                onClick: () => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  const navigationState: any = {
                    activeTab: 'standings',
                    selectedTournament: selectedTournament,
                    allTournaments: (location.state as any)?.allTournaments
                  };
                  
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: navigationState
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: navigationState
                    });
                  }
                }
              },
              { label: `${ageCategoryInfo?.ageCategory || 'Age Category'} - Group Standings`, active: true }
            ]}
          />
        </div>

        {/* Header - Match TournamentDetailPage styling */}
        <div style={{ padding: '0 15px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={() => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  // Ensure selectedTournament always has sports property for TournamentDetailPage
                  const finalSelectedTournament = selectedTournament?.sports 
                    ? selectedTournament 
                    : (currentSport ? { sports: currentSport } : (selectedTournament || { sports: '' }));
                  
                  const navigationState: any = {
                    activeTab: 'standings',
                    selectedTournament: finalSelectedTournament,
                    allTournaments: (location.state as any)?.allTournaments
                  };
                  
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: navigationState
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: navigationState
                    });
                  }
                }}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="tournament-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
                <div className="default-tournament-logo-small">
                  <i className="ri-group-line"></i>
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>
                  {ageCategoryInfo?.ageCategory || 'Age Category'} - Group Standings
                </h1>
                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                  <span className="tournament-short-name-badge">Group</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '0 15px' }}>
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ri-group-line me-2"></i>
                Group Standings
              </h5>
              <Button
                variant="primary"
                onClick={handleOpenAddGroupCard}
                className="d-flex align-items-center"
              >
                <i className="ri-add-line me-2"></i>
                Add Groups
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Error Message */}
              {error && (
                <div className="alert alert-danger mb-3">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </div>
              )}

              {/* Add Group Card - Above Table */}
              {showAddGroupCard && (
                <div className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom-0">
                      <h5 className="mb-0 fw-semibold">
                        <i className="ri-add-circle-line me-2 text-primary"></i>
                        Add Groups
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <TeamManagementCard
                        tournamentId={tournamentId!}
                        ageCategoryId={ageCategoryId!}
                        ageCategoryName={ageCategoryInfo?.ageCategory}
                        standings={[]} // Empty for new group
                        allGroupStandings={groupStandings.map(group => group.standings)}
                        onStandingsUpdate={handleAddGroup}
                        groupName={newGroupName}
                        contextTitle={`Group ${newGroupName}`}
                        tournamentSeasonId={tournamentSeasonId} // Pass tournamentSeasonId to filter teams by season
                        showCard={true}
                        onCloseCard={() => setShowAddGroupCard(false)}
                        isEditMode={false}
                        onSetEditMode={() => {}}
                        addingTeams={addingGroup}
                        onSetAddingTeams={setAddingGroup}
                        showGroupNameInput={true}
                        onGroupNameChange={setNewGroupName}
                      />
                    </Card.Body>
                  </Card>
                </div>
              )}

              {/* Groups Table */}
              <div className="manage-tournaments-table-container">
                <Table className="manage-tournaments-table groups-table mb-0">
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Teams</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGroups.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-5">
                          <div className="text-muted">
                            <i className="ri-inbox-line" style={{ fontSize: '48px', opacity: 0.3 }}></i>
                            <p className="mt-2 mb-0">No groups found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredGroups.map((group) => (
                        <tr key={group.groupName}>
                          <td>
                            <strong className="text-dark">Group {group.groupName}</strong>
                          </td>
                          <td>
                            <span className="text-muted">{group.standings.length} Teams</span>
                          </td>
                          <td>
                            <div className="tournament-actions">
                              <button
                                className="action-btn btn-view"
                                onClick={() => handleViewGroup(group)}
                                title="View Group Details"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              <button
                                className="action-btn btn-edit"
                                onClick={() => handleEditGroup(group)}
                                title="Edit Group Teams"
                              >
                                <i className="ri-pencil-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default GroupStandingsDetailPage;